import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useUserData } from "@/hooks/useUserData";
import type { MovieData } from "@/components/SwipeCard";
import TmdbAttribution from "@/components/TmdbAttribution";

interface ReviewItem extends MovieData {
  media_type?: string;
}

type SentimentKey = "hated" | "not_great" | "ok" | "pretty_good" | "loved";

const ReviewPending = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getReviewPending, removeFromReviewPending, reviewMovie, getWatchHistoryRating } = useUserData();
  const [items, setItems] = useState<ReviewItem[]>([]);
  const [reviewingMovie, setReviewingMovie] = useState<ReviewItem | null>(null);
  const [existingRating, setExistingRating] = useState<SentimentKey | null>(null);
  const [loadingRating, setLoadingRating] = useState(false);
  const [loading, setLoading] = useState(true);

  const sentiments = [
    { key: "hated" as const, emoji: "😡", label: t("watchHistory.hatedIt") },
    { key: "not_great" as const, emoji: "😕", label: t("watchHistory.notGreat") },
    { key: "ok" as const, emoji: "😐", label: t("watchHistory.ok") },
    { key: "pretty_good" as const, emoji: "😊", label: t("watchHistory.prettyGood") },
    { key: "loved" as const, emoji: "😍", label: t("watchHistory.lovedIt") },
  ];

  useEffect(() => {
    getReviewPending()
      .then(setItems)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getReviewPending]);

  const openReview = async (movie: ReviewItem) => {
    setReviewingMovie(movie);
    setExistingRating(null);
    setLoadingRating(true);
    try {
      const rating = await getWatchHistoryRating(movie.id);
      if (rating && sentiments.some((s) => s.key === rating)) {
        setExistingRating(rating as SentimentKey);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingRating(false);
    }
  };

  const handleReview = async (movie: ReviewItem, rating: SentimentKey) => {
    await reviewMovie(movie, rating, movie.media_type || "movie");
    setItems((prev) => prev.filter((i) => i.id !== movie.id));
    setReviewingMovie(null);
    setExistingRating(null);
  };

  const handleRemove = async (movie: ReviewItem) => {
    await removeFromReviewPending(movie.id);
    setItems((prev) => prev.filter((i) => i.id !== movie.id));
    setReviewingMovie(null);
    setExistingRating(null);
  };

  const existingSentiment = existingRating ? sentiments.find((s) => s.key === existingRating) : null;

  return (
    <div className="h-[100dvh] bg-background flex flex-col pb-14" style={{ paddingBottom: "calc(3.5rem + env(safe-area-inset-bottom, 0px))" }}>
      {/* Header */}
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3"
        >
          <button
            onClick={() => navigate("/home")}
            className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center active:scale-95 transition-transform"
          >
            <ChevronLeft className="w-5 h-5 text-foreground" />
          </button>
          <div>
            <h1 className="font-display text-xl font-bold text-foreground">{t("review.title")}</h1>
            <p className="text-xs text-muted-foreground">{t("review.subtitle")}</p>
          </div>
        </motion.div>
      </div>

      {/* Content */}
      <div
        className="flex-1 min-h-0 overflow-y-auto px-4"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-xl bg-secondary/50 border border-border p-8 text-center mt-4">
            <p className="text-3xl mb-2">🎉</p>
            <p className="text-sm text-muted-foreground">{t("review.allCaughtUp")}</p>
          </div>
        ) : (
          <div className="space-y-3 pb-6">
            {items.map((movie, i) => (
              <motion.button
                key={movie.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => openReview(movie)}
                className="w-full flex items-center gap-3 p-3 rounded-xl bg-secondary/50 border border-border hover:border-primary/30 transition-all active:scale-[0.98] text-left"
              >
                <div className="w-14 h-20 flex-shrink-0 rounded-lg overflow-hidden border border-border">
                  <img src={movie.poster} alt={movie.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{movie.title}</p>
                  <p className="text-xs text-muted-foreground">{movie.year}</p>
                  <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary">
                    {(movie.media_type || "movie") === "tv" ? t("review.tvShowLabel") : t("review.movieLabel")}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">{t("review.tapToRate")}</span>
              </motion.button>
            ))}
          </div>
        )}
        <TmdbAttribution className="pt-6 pb-2" />
        <p className="text-[10px] text-muted-foreground/60 text-center mb-4">© 2026 Ayeris.tv — All rights reserved.</p>
      </div>

      {/* Review modal */}
      <AnimatePresence>
        {reviewingMovie && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => { setReviewingMovie(null); setExistingRating(null); }}
          >
            <motion.div
              initial={{ y: 200 }}
              animate={{ y: 0 }}
              exit={{ y: 200 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-card border border-border rounded-2xl p-6 space-y-5"
            >
              {/* Movie info */}
              <div className="flex items-center gap-3">
                <div className="w-16 h-24 flex-shrink-0 rounded-lg overflow-hidden border border-border">
                  <img src={reviewingMovie.poster} alt={reviewingMovie.title} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-foreground text-lg truncate">{reviewingMovie.title}</p>
                  <p className="text-sm text-muted-foreground">{reviewingMovie.year}</p>
                </div>
                <button
                  onClick={() => { setReviewingMovie(null); setExistingRating(null); }}
                  className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>

              {loadingRating ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : existingRating && existingSentiment ? (
                <>
                  <div className="text-center space-y-1">
                    <p className="text-sm text-muted-foreground">{t("review.watchedBefore")}</p>
                    <p className="text-sm text-foreground">
                      {t("review.previousRating")} <span className="text-lg">{existingSentiment.emoji}</span> <span className="font-medium">{existingSentiment.label}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{t("review.changeRatingHint")}</p>
                  </div>

                  <div className="grid grid-cols-5 gap-2">
                    {sentiments.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => handleReview(reviewingMovie, s.key)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all active:scale-95 ${
                          s.key === existingRating
                            ? "border-primary bg-primary/10"
                            : "border-border bg-secondary hover:border-primary/50"
                        }`}
                      >
                        <span className="text-3xl">{s.emoji}</span>
                        <span className="text-xs font-medium text-foreground">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground text-center">{t("review.howWasIt")}</p>

                  <div className="grid grid-cols-5 gap-2">
                    {sentiments.map((s) => (
                      <button
                        key={s.key}
                        onClick={() => handleReview(reviewingMovie, s.key)}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-border bg-secondary hover:border-primary/50 active:scale-95 transition-all"
                      >
                        <span className="text-3xl">{s.emoji}</span>
                        <span className="text-xs font-medium text-foreground">{s.label}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <button
                onClick={() => handleRemove(reviewingMovie)}
                className="w-full py-3 rounded-xl border border-border text-sm text-muted-foreground hover:text-foreground hover:border-destructive/50 transition-all active:scale-[0.98]"
              >
                {t("review.didntWatch")}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewPending;
